import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { Textarea } from '@ordo/ui/textarea'
import { AsyncCombobox } from 'compositions/AsyncCombobox'
import { useTemplate, useUploadAttachment } from 'hooks/api'
import { useAsyncLoader } from 'hooks/useAsyncLoader'
import { Paperclip, Upload, X } from 'lucide-react'
import { useCallback, useMemo, useRef, useState } from 'react'
import type { Attachment } from 'types/attachments'
import type { CommunicationMethod } from 'types/communications'
import type { Template } from 'types/templates'

import { type Recipient, RecipientSelector } from '../communications/recipient-selector'
import { RRuleBuilder } from './rrule-builder'

export interface ScheduleFormData {
  name: string
  method: CommunicationMethod
  subject: string
  templateId: string
  eventData: string
  rrule: string
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function ScheduleFormFields({
  data,
  onChange,
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  attachments,
  onAttachmentsChange,
}: {
  data: ScheduleFormData
  onChange: (field: keyof ScheduleFormData, value: string) => void
  recipients: Recipient[]
  onAddRecipient: (recipient: Recipient) => void
  onRemoveRecipient: (recipient: Recipient) => void
  attachments: Attachment[]
  onAttachmentsChange: (attachments: Attachment[]) => void
}) {
  const loadTemplates = useAsyncLoader<Template>('/api/templates', { active: true })
  const { data: selectedTemplate } = useTemplate(data.templateId || undefined)
  const selectedTemplateItem = useMemo<Template | null>(
    () => (data.templateId && selectedTemplate?.id === data.templateId ? selectedTemplate : null),
    [data.templateId, selectedTemplate],
  )
  const { perform: uploadAttachment, loading: uploading } = useUploadAttachment()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [eventDataError, setEventDataError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleRRuleChange = useCallback(
    (rrule: string) => {
      onChange('rrule', rrule)
    },
    [onChange],
  )

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files || files.length === 0) {
        return
      }
      setUploadError(null)
      const newAttachments: Attachment[] = []
      for (const file of Array.from(files)) {
        try {
          const uploaded = await uploadAttachment(file)
          newAttachments.push(uploaded)
        } catch (err) {
          setUploadError(err instanceof Error ? err.message : 'Upload failed')
        }
      }
      if (newAttachments.length > 0) {
        onAttachmentsChange([...attachments, ...newAttachments])
      }
      // Reset input so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [attachments, onAttachmentsChange, uploadAttachment],
  )

  const handleRemoveAttachment = useCallback(
    (id: string) => {
      onAttachmentsChange(attachments.filter((a) => a.id !== id))
    },
    [attachments, onAttachmentsChange],
  )

  const handleEventDataChange = useCallback(
    (value: string) => {
      onChange('eventData', value)
      if (!value.trim()) {
        setEventDataError(null)
        return
      }
      try {
        JSON.parse(value)
        setEventDataError(null)
      } catch {
        setEventDataError('Invalid JSON')
      }
    },
    [onChange],
  )

  return (
    <div className="flex flex-col gap-6">
      {/* Communication details section */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-muted-foreground">Communication</h3>
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Name */}
            <div className="space-y-1.5">
              <label htmlFor="commName" className="text-sm font-medium text-muted-foreground">
                Name
              </label>
              <Input id="commName" value={data.name} onChange={(e) => onChange('name', e.target.value)} />
            </div>

            {/* Method */}
            <div className="space-y-1.5">
              <span className="text-sm font-medium text-muted-foreground">Delivery Method</span>
              <Select value={data.method} onValueChange={(v) => onChange('method', v)}>
                <SelectTrigger className="w-full text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Template selector */}
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Template</span>
            <AsyncCombobox<Template>
              value={selectedTemplateItem}
              onChange={(item) => onChange('templateId', item?.id ?? '')}
              load={loadTemplates}
              getKey={(item) => item.id}
              getLabel={(item) => item.name}
              placeholder="Select a template"
              searchPlaceholder="Search templates…"
              emptyMessage="No templates available"
            />
          </div>

          {/* Recipients */}
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Recipients</span>
            <RecipientSelector recipients={recipients} onAdd={onAddRecipient} onRemove={onRemoveRecipient} />
          </div>

          {/* Subject — only for EMAIL */}
          {data.method === 'EMAIL' && (
            <div className="space-y-1.5">
              <label htmlFor="commSubject" className="text-sm font-medium text-muted-foreground">
                Subject
              </label>
              <Input id="commSubject" value={data.subject} onChange={(e) => onChange('subject', e.target.value)} />
            </div>
          )}

          {/* Event Data (JSON) */}
          <div className="space-y-1.5">
            <label htmlFor="eventData" className="text-sm font-medium text-muted-foreground">
              Event Data (JSON)
            </label>
            <Textarea
              id="eventData"
              value={data.eventData}
              onChange={(e) => handleEventDataChange(e.target.value)}
              placeholder='{"key": "value"}'
              className="font-mono text-sm"
              rows={3}
            />
            {eventDataError && <p className="text-xs text-destructive">{eventDataError}</p>}
            <p className="text-xs text-muted-foreground">
              Optional JSON object passed to the template as {'{{ event.key }}'}.
            </p>
          </div>

          {/* Attachments */}
          <div className="space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">Attachments</span>
            <div className="flex flex-col gap-2">
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {attachments.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                      <Paperclip className="h-3 w-3" />
                      {a.filename}
                      <span className="text-muted-foreground">({formatFileSize(a.fileSize)})</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(a.id)}
                        className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-destructive/20 hover:text-destructive">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}>
                <Upload className="mr-1 h-4 w-4" />
                {uploading ? 'Uploading...' : 'Upload Files'}
              </Button>
              {uploadError && <p className="text-xs text-destructive">{uploadError}</p>}
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Recurrence section */}
      <div>
        <h3 className="mb-3 text-sm font-medium text-muted-foreground">Recurrence</h3>
        <RRuleBuilder value={data.rrule} onChange={handleRRuleChange} />
      </div>
    </div>
  )
}
